// Display glyph for a frame: character text, or — when the stored character is
// an unfaithful Unicode approximation (kanji.glyph_from_strokes) — the curated
// host stroke-slice rendered as an inline SVG. The slice is the SAME curation
// the stroke strip and draw grader consume (host-substitution spec §2), so the
// display glyph can never drift from what the learner draws.
//
// Sizing: the SVG fills a 1em square by default so it drops into text-sized
// slots; pass className to override (e.g. the Plate's fixed-px glyph box).
// Color: strokes inherit currentColor, so status-ink classes and hover
// transitions on the surrounding element keep working.

export type DisplayGlyph =
    | { kind: 'text'; character: string }
    | { kind: 'svg'; strokePaths: string[]; viewBox: string }

/**
 * Derives a frame's display glyph from an already-loaded stroke slice —
 * for surfaces that ship slice data to the client anyway (Review's and
 * Learn's DrawCanvas maps). Server surfaces without slice data at hand use
 * lib/data/display-glyph.ts resolveDisplayGlyph instead; both funnel every
 * non-flagged (or slice-less) frame to plain character text.
 */
export function displayGlyphFrom(
    k: { character: string; glyphFromStrokes: boolean },
    slice: { strokePaths: string[]; viewBox: string } | null
): DisplayGlyph {
    if (!k.glyphFromStrokes || !slice)
        return { kind: 'text', character: k.character }
    return {
        kind: 'svg',
        strokePaths: slice.strokePaths,
        viewBox: slice.viewBox,
    }
}

// 109-space stroke width tuned to sit next to font-mincho text weight —
// KanjiVG's native 3 reads skeletal beside a serif glyph at display sizes.
const GLYPH_STROKE_WIDTH = 7

export function FrameGlyph({
    glyph,
    label,
    className,
}: {
    glyph: DisplayGlyph
    label?: string
    className?: string
}) {
    if (glyph.kind === 'text') {
        return (
            <span aria-label={label} className={className}>
                {glyph.character}
            </span>
        )
    }
    return (
        <svg
            viewBox={glyph.viewBox}
            role="img"
            aria-label={label}
            className={className ?? 'inline-block size-[1em]'}
        >
            {glyph.strokePaths.map((d, i) => (
                <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={GLYPH_STROKE_WIDTH}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ))}
        </svg>
    )
}
